import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { ManualTaskDialog } from '../components/linkedin/ManualTaskDialog';
import { leadsAPI } from '../lib/api';
import {
  Linkedin, Loader2, RefreshCw, Copy, ExternalLink, CheckCircle2,
  Sparkles, Trash2, Search, Filter, Send, Clock, ArrowRight,
  BarChart3, Users, Zap, Edit3, Save, X, ChevronDown, Plus, UserPlus, AlertCircle,
  ShieldAlert, Settings, Play, Download, UserCheck
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

// Relative "in Xd / Xh / Xm" label from an ISO timestamp (for accept-gate chips).
function untilLabel(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  if (ms <= 0) return 'due now';
  const days = Math.floor(ms / 86400000);
  const hrs = Math.floor(ms / 3600000);
  if (days >= 1) return `in ${days}d`;
  if (hrs >= 1) return `in ${hrs}h`;
  return `in ${Math.max(1, Math.floor(ms / 60000))}m`;
}

function heartbeatAge(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return 'just now';
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor(ms / 1000);
  if (mins >= 60) return `${Math.floor(mins / 60)}h ago`;
  if (mins >= 1) return `${mins}m ago`;
  return `${secs}s ago`;
}

// Absolute "Mon D, h:mm AM" label for the time a task was actually sent/completed.
function sentAtLabel(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// ─── Extension Status Banner (Phase 1.1) ─────────────────
function ExtensionStatusBanner() {
  const [state, setState] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const st = await api.get('/api/linkedin/extension/status');
        let usage = {};
        try { usage = (await api.get('/api/linkedin/usage/today')).data || {}; } catch { /* non-fatal */ }
        if (!alive) return;
        setState({ ...(st.data || {}), ...usage });
      } catch {
        // 402/403 on non-growth plans, or network — fail soft, hide the banner.
        if (alive) setHidden(true);
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (hidden || !state) return null;

  const connected = state.status === 'connected';
  const connects = state.connects_today ?? 0;
  const limit = state.daily_connect_limit ?? 22;
  const hb = heartbeatAge(state.last_heartbeat);

  return (
    <div
      className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-sm ${
        connected
          ? 'border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-950/20'
          : 'border-rose-500/30 bg-rose-50/50 dark:bg-rose-950/20'
      }`}
      data-testid="extension-status-banner"
    >
      <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${connected ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`} />
      {connected ? (
        <p className="text-foreground/90" data-testid="extension-status-on">
          <span className="font-semibold text-emerald-600 dark:text-emerald-400">Auto-pilot ON</span>
          {hb ? <> · heartbeat {hb}</> : null}
          <> · {connects}/{limit} connects today</>
          {state.extension_version ? <span className="ml-1 text-xs text-muted-foreground" data-testid="extension-version-chip"> · ext v{state.extension_version}</span> : null}
          {state.login_required ? <span className="ml-2 inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300" data-testid="extension-login-required-chip">Sign in to LinkedIn in the pinned tab — tasks are waiting</span> : null}
        </p>
      ) : (
        <p className="text-foreground/90" data-testid="extension-status-off">
          <span className="font-semibold text-rose-600 dark:text-rose-400">Extension idle</span>
          {' — '}open the Lockene extension popup and press <span className="font-medium">Start</span> to send queued tasks automatically.
        </p>
      )}
    </div>
  );
}

// ─── Automation Controls: restriction auto-pause + send window (Phase 3) ──
function AutomationBar() {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/api/linkedin-tasks/automation-settings');
      setS(r.data);
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const resume = async () => {
    try { await api.post('/api/linkedin-tasks/resume'); toast.success('Sending resumed'); load(); }
    catch { toast.error('Failed to resume'); }
  };

  const save = async (patch) => {
    setSaving(true);
    try {
      await api.put('/api/linkedin-tasks/automation-settings', patch);
      await load();
      toast.success('Send window updated');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (!s) return null;
  const w = s.send_window || {};
  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <>
      {s.paused && (
        <div className="flex items-center gap-3 rounded-lg border border-rose-500/40 bg-rose-50/60 dark:bg-rose-950/25 px-4 py-3"
          data-testid="restriction-banner">
          <ShieldAlert className="h-5 w-5 text-rose-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-rose-700 dark:text-rose-300">Sending paused — LinkedIn flagged a restriction</p>
            <p className="text-xs text-rose-600/80 dark:text-rose-400/80 truncate">
              {s.pause_reason || 'Resolve the restriction on LinkedIn, then resume.'}
            </p>
          </div>
          <Button size="sm" className="h-8 gap-1.5 bg-rose-600 hover:bg-rose-700 text-white shrink-0"
            onClick={resume} data-testid="resume-sending-btn">
            <Play className="h-3.5 w-3.5" /> Resume sending
          </Button>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span data-testid="send-window-status">
          {s.send_window_enabled
            ? <>Send window <span className="font-medium text-foreground">ON</span> · {String(w.start_hour).padStart(2, '0')}:00–{String(w.end_hour).padStart(2, '0')}:00 {w.timezone} {s.window_open ? '· open now' : '· closed (only "Send Now" goes out)'}</>
            : <>Send window <span className="font-medium text-foreground">OFF</span> · sending 24/7</>}
        </span>
        <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs" onClick={() => setOpen(true)} data-testid="send-window-settings-btn">
          <Settings className="h-3.5 w-3.5" /> Send window
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="send-window-dialog">
          <DialogHeader><DialogTitle>Working-hours send window</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Only send during working hours</p>
                <p className="text-xs text-muted-foreground">Off = the extension sends around the clock (default).</p>
              </div>
              <Switch checked={!!s.send_window_enabled} disabled={saving}
                onCheckedChange={(v) => save({ enabled: v })} data-testid="send-window-toggle" />
            </div>
            {s.send_window_enabled && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-xs space-y-1">
                    <span className="text-muted-foreground">Start hour (0–23)</span>
                    <Input type="number" min={0} max={23} defaultValue={w.start_hour}
                      onBlur={(e) => save({ start_hour: parseInt(e.target.value, 10) })} data-testid="send-window-start" />
                  </label>
                  <label className="text-xs space-y-1">
                    <span className="text-muted-foreground">End hour (1–24)</span>
                    <Input type="number" min={1} max={24} defaultValue={w.end_hour}
                      onBlur={(e) => save({ end_hour: parseInt(e.target.value, 10) })} data-testid="send-window-end" />
                  </label>
                </div>
                <label className="text-xs space-y-1 block">
                  <span className="text-muted-foreground">Timezone (IANA, e.g. America/New_York)</span>
                  <Input defaultValue={w.timezone}
                    onBlur={(e) => save({ timezone: e.target.value.trim() })} data-testid="send-window-tz" />
                </label>
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">Active days</span>
                  <div className="flex gap-1.5 flex-wrap">
                    {DAYS.map((d, i) => {
                      const on = (w.days || []).includes(i);
                      return (
                        <button key={d} type="button" disabled={saving}
                          className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${on ? 'bg-sky-600 text-white border-sky-600' : 'border-input text-muted-foreground hover:bg-muted'}`}
                          onClick={() => {
                            const days = new Set(w.days || []);
                            on ? days.delete(i) : days.add(i);
                            save({ days: Array.from(days).sort() });
                          }}
                          data-testid={`send-window-day-${i}`}>
                          {d}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Stats Cards ─────────────────────────────────────────
function StatsBar({ stats }) {
  const items = [
    { label: 'Total Tasks', value: stats.total, icon: Linkedin, color: 'text-sky-500' },
    { label: 'Pending', value: stats.pending, icon: Clock, color: 'text-amber-500' },
    { label: 'Completed', value: stats.completed, icon: CheckCircle2, color: 'text-emerald-500' },
    { label: 'Sequences', value: stats.sequences, icon: ArrowRight, color: 'text-violet-500' },
    { label: 'Completion Rate', value: `${stats.completion_rate}%`, icon: BarChart3, color: 'text-sky-500' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="linkedin-stats">
      {items.map((item, i) => (
        <Card key={i} className="border-border/50">
          <CardContent className="p-3 flex items-center gap-3">
            <item.icon className={`h-5 w-5 ${item.color} shrink-0`} />
            <div>
              <p className="text-lg font-bold leading-none">{item.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{item.label}</p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Editable Task Card ──────────────────────────────────
const BLOCK_REASON_LABELS = {
  lead_unscored: 'Waiting for AI score',
  lead_disqualified: 'Lead disqualified',
  lead_terminal: 'Lead closed',
  lead_missing: 'Lead deleted',
  daily_connect_cap: 'Daily connect cap',
  daily_message_cap: 'Daily message cap',
  linkedin_logged_out: 'Sign in to LinkedIn',
};
const FAILURE_LABELS = {
  weekly_limit: 'Weekly limit',
  wrong_person: 'Wrong-person guard',
  restricted: 'Account restriction',
  invite_ui: 'Invite dialog',
  no_connect_option: 'No Connect option',
  message_ui: 'Messaging blocked',
  page_timeout: 'Page timeout',
  tab_not_ready: 'Tab not ready',
  wrong_page: 'Profile unreachable',
  bad_url: 'URL repaired',
  no_url: 'Missing URL',
  not_picked_up: 'Extension offline',
  logged_out: 'LinkedIn logged out',
  page_changed: 'Page changed mid-task',
  connect_not_found: 'Connect not found',
  other: 'Failed',
  unknown: 'Unknown cause',
};

function TaskCard({ task, onCopy, onToggle, onMarkSent, onRegenerate, onDelete, onEdit, onApprove, onRetry, onSendNow, regenerating }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(task.connection_message);
  const isCompleted = task.status === 'completed';
  const isScheduled = task.status === 'scheduled';
  const needsApproval = task.status === 'needs_approval';
  const isFailed = task.status === 'failed';
  const isSkipped = task.status === 'skipped';
  const isAcceptCheck = task.action === 'check_accept';
  // Only a plain standalone note can be undone; connect/message/sequence sends
  // schedule accept-checks or advance the cadence, so they show a static "Sent".
  const canUndo = !task.sequence_id && task.action !== 'connect' && task.action !== 'message';

  const handleSaveEdit = async () => {
    await onEdit(task.id, editText);
    setEditing(false);
  };

  return (
    <div
      className={`border rounded-lg p-4 space-y-3 transition-colors ${
        isCompleted ? 'border-emerald-500/20 bg-emerald-50/30 dark:bg-emerald-950/10'
        : isScheduled ? 'border-violet-500/20 bg-violet-50/30 dark:bg-violet-950/10'
        : isFailed ? 'border-rose-500/30 bg-rose-50/40 dark:bg-rose-950/15'
        : 'hover:border-sky-500/30'
      }`}
      data-testid={`linkedin-task-${task.id}`}
    >
      {/* Header Row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Badge variant="outline" className={`text-[10px] shrink-0 ${
            isCompleted ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30'
            : isScheduled ? 'bg-violet-500/10 text-violet-500 border-violet-500/30'
            : isFailed ? 'bg-rose-500/10 text-rose-500 border-rose-500/30'
            : needsApproval ? 'bg-amber-500/10 text-amber-500 border-amber-500/30'
            : 'bg-sky-500/10 text-sky-500 border-sky-500/30'
          }`} data-testid="task-status-badge">
            {isCompleted ? 'Sent' : isScheduled ? 'Scheduled' : isFailed ? 'Failed' : needsApproval ? 'Awaiting Approval' : 'Pending'}
          </Badge>
          {task.action && task.action !== 'connect' && (
            <Badge variant="outline" className="text-[10px]">{isAcceptCheck ? 'accept check' : task.action}</Badge>
          )}
          {task.auto_triggered && task.action !== 'connect' && (
            <Badge variant="outline" className="text-[10px] bg-fuchsia-500/10 text-fuchsia-500 border-fuchsia-500/30">AI Reply</Badge>
          )}
          {task.auto_triggered && task.action === 'connect' && (
            <Badge variant="outline" className="text-[10px] bg-sky-500/10 text-sky-500 border-sky-500/30">AI Cadence</Badge>
          )}
          {task.sequence_step && (
            <Badge variant="secondary" className="text-[10px]">Step {task.sequence_step}</Badge>
          )}
          {task.status === 'pending' && task.block_reason && BLOCK_REASON_LABELS[task.block_reason] && (
            <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-500 border-amber-500/30"
              title="LEO is holding this task until the block clears" data-testid={`task-block-reason-${task.id}`}>
              {BLOCK_REASON_LABELS[task.block_reason]}
            </Badge>
          )}
          <span className="text-sm font-medium truncate">{task.lead_name}</span>
          {task.lead_title && (
            <span className="text-xs text-muted-foreground truncate hidden md:inline">
              {task.lead_title} {task.lead_company ? `@ ${task.lead_company}` : ''}
            </span>
          )}
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive/60 hover:text-destructive shrink-0"
          onClick={() => onDelete(task.id)} data-testid="task-delete-btn">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Message Body */}
      {editing ? (
        <div className="space-y-2">
          <textarea
            className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            data-testid="task-edit-textarea"
          />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setEditText(task.connection_message); }}>
              <X className="h-3 w-3 mr-1" /> Cancel
            </Button>
            <Button size="sm" onClick={handleSaveEdit} data-testid="task-save-edit-btn">
              <Save className="h-3 w-3 mr-1" /> Save
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-muted/50 rounded-md p-3 cursor-pointer group" onClick={() => !isCompleted && setEditing(true)}
          data-testid="task-message-body">
          <p className="text-sm leading-relaxed text-foreground/90">{task.connection_message}</p>
          {!isCompleted && (
            <p className="text-[10px] text-muted-foreground mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
              Click to edit
            </p>
          )}
        </div>
      )}

      {/* Actions Row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Button variant="outline" size="sm" className="h-7 text-xs px-2.5 gap-1.5"
            onClick={() => onCopy(task.connection_message)} data-testid="task-copy-btn">
            <Copy className="h-3 w-3" /> Copy
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs px-2.5 gap-1.5"
            onClick={() => onRegenerate(task.id)} disabled={regenerating} data-testid="task-regenerate-btn">
            {regenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Regenerate
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs px-2.5 gap-1.5"
            onClick={() => !editing && setEditing(true)} data-testid="task-edit-btn">
            <Edit3 className="h-3 w-3" /> Edit
          </Button>
          {task.linkedin_url && (
            <Button variant="outline" size="sm" className="h-7 text-xs px-2.5 gap-1.5"
              onClick={() => window.open(task.linkedin_url, '_blank')} data-testid="task-profile-btn">
              <ExternalLink className="h-3 w-3" /> Profile
            </Button>
          )}
          {(task.status === 'pending' || isScheduled || isFailed) && !isAcceptCheck && (
            <Button variant="outline" size="sm"
              className="h-7 text-xs px-2.5 gap-1.5 border-sky-500/40 text-sky-600 hover:bg-sky-500/10"
              onClick={() => onSendNow(task.id)} data-testid="task-sendnow-btn"
              title="Push to the front of the queue — the extension sends it on its next check">
              <Zap className="h-3 w-3" /> {task.priority ? 'Queued next' : 'Send Now'}
            </Button>
          )}
        </div>
        {needsApproval ? (
          <Button
            size="sm"
            className="h-7 text-xs px-3 gap-1.5 bg-amber-600 hover:bg-amber-700 text-white"
            onClick={() => onApprove(task.id)}
            data-testid="task-approve-btn"
          >
            <CheckCircle2 className="h-3 w-3" /> Approve & Queue
          </Button>
        ) : isAcceptCheck ? (
          <span className="text-[11px] font-medium text-violet-600 dark:text-violet-400 flex items-center gap-1" data-testid="task-accept-chip">
            <Clock className="h-3 w-3" /> Waiting for accept · rechecks {untilLabel(task.scheduled_at) || 'soon'}
          </span>
        ) : isCompleted ? (
          <div className="flex items-center gap-2">
            {sentAtLabel(task.completed_at || task.executed_at || task.updated_at) && (
              <span className="text-[11px] text-muted-foreground whitespace-nowrap" data-testid="task-sent-time">
                {sentAtLabel(task.completed_at || task.executed_at || task.updated_at)}
              </span>
            )}
            {canUndo ? (
              <Button
                variant="ghost" size="sm" className="h-7 text-xs px-3 gap-1.5"
                onClick={() => onToggle(task.id)} data-testid="task-toggle-btn"
              >
                <CheckCircle2 className="h-3 w-3" /> Undo
              </Button>
            ) : (
              <span className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1" data-testid="task-sent-label">
                <CheckCircle2 className="h-3 w-3" /> Sent
              </span>
            )}
          </div>
        ) : isFailed ? (
          <Button
            size="sm"
            className="h-7 text-xs px-3 gap-1.5 bg-rose-600 hover:bg-rose-700 text-white"
            onClick={() => onRetry(task.id)}
            data-testid="task-retry-btn"
          >
            <RefreshCw className="h-3 w-3" /> Retry
          </Button>
        ) : (
          <Button
            size="sm"
            className="h-7 text-xs px-3 gap-1.5 bg-sky-600 hover:bg-sky-700 text-white"
            onClick={() => onMarkSent(task.id)}
            data-testid="task-marksent-btn"
          >
            <CheckCircle2 className="h-3 w-3" /> Mark Sent
          </Button>
        )}
      </div>

      {/* Failure reason chip (Phase 2.2 + classified causes) */}
      {isFailed && (
        <p className="text-[10px] text-rose-600 dark:text-rose-400 flex items-center gap-1.5" data-testid="task-failed-chip">
          <AlertCircle className="h-3 w-3 shrink-0" />
          {task.failure_code && (
            <span className="shrink-0 rounded bg-rose-500/15 px-1.5 py-0.5 font-semibold uppercase tracking-wide text-[9px]"
              data-testid="task-failure-code">
              {FAILURE_LABELS[task.failure_code] || 'Failed'}
            </span>
          )}
          <span>{task.failure_reason || task.execution_error || 'Send failed'}</span>
        </p>
      )}

      {/* Skipped chip — LinkedIn's choice (Follow-only / invite-restricted), not a failure */}
      {isSkipped && (
        <p className="text-[10px] text-amber-600 dark:text-amber-400 flex items-center gap-1.5" data-testid="task-skipped-chip">
          <AlertCircle className="h-3 w-3 shrink-0" />
          <span className="shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 font-semibold uppercase tracking-wide text-[9px]">
            Skipped · Follow-only
          </span>
          <span>{task.skip_reason || task.failure_reason || 'This profile does not accept connection requests.'}</span>
        </p>
      )}

      {/* Accept-gate / next-step chips (Phase 1.3) */}
      {isScheduled && !isAcceptCheck && (
        <p className="text-[10px] text-violet-600 dark:text-violet-400 flex items-center gap-1" data-testid="task-nextstep-chip">
          <Clock className="h-3 w-3" /> Next step {untilLabel(task.scheduled_at) || 'scheduled'}
        </p>
      )}

      {/* Delay Info */}
      {task.delay_days > 0 && !isCompleted && !isScheduled && (
        <p className="text-[10px] text-muted-foreground flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Send {task.delay_days} days after previous step
        </p>
      )}
    </div>
  );
}

// ─── Main LinkedIn Outbox Page ───────────────────────────
export default function LinkedInOutbox({ embedded = false }) {
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState(null);
  const [stats, setStats] = useState({ total: 0, pending: 0, scheduled: 0, completed: 0, connections: 0, messages: 0, sequences: 0, completion_rate: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('pending');
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [retryingAll, setRetryingAll] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [recheckingNow, setRecheckingNow] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerResults, setPickerResults] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [manualLead, setManualLead] = useState(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const t = setTimeout(async () => {
      setPickerLoading(true);
      try {
        const res = await leadsAPI.getAll({ search: pickerSearch || undefined, limit: 20 });
        setPickerResults(res.data || []);
      } catch { setPickerResults([]); } finally { setPickerLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [pickerOpen, pickerSearch]);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const [tasksRes, statsRes] = await Promise.all([
        api.get('/api/linkedin-tasks?limit=1000'),
        api.get('/api/linkedin-tasks/stats/overview'),
      ]);
      setTasks(tasksRes.data?.tasks || []);
      setCounts(tasksRes.data?.counts || null);
      setStats(statsRes.data || stats);
    } catch (err) {
      console.error('Failed to load LinkedIn tasks:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const handleExportCsv = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/linkedin-tasks/export?status=completed`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'linkedin_outreach.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Sent LinkedIn outreach exported (CSV)');
    } catch {
      toast.error('Failed to export');
    }
  };

  const handleCopy = (message) => {
    navigator.clipboard.writeText(message);
    toast.success('Message copied to clipboard');
  };

  const handleToggle = async (taskId) => {
    try {
      const res = await api.put(`/api/linkedin-tasks/${taskId}/status`);
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: res.data.status } : t));
      toast.success(res.data.status === 'completed' ? 'Marked as sent' : 'Marked as pending');
      loadTasks(); // Refresh stats
    } catch { toast.error('Failed to update'); }
  };

  const handleMarkSent = async (taskId) => {
    try {
      const res = await api.put(`/api/linkedin-tasks/${taskId}/mark-sent`);
      if (res.data?.no_op) { loadTasks(); return; }
      toast.success(res.data?.lifecycle
        ? 'Marked sent — cadence advanced (accept-check scheduled)'
        : 'Marked as sent');
      loadTasks();
    } catch { toast.error('Failed to mark sent'); }
  };

  const handleApprove = async (taskId) => {
    try {
      await api.put(`/api/linkedin-tasks/${taskId}/approve`);
      toast.success('Reply approved — the extension will send it');
      loadTasks();
    } catch { toast.error('Failed to approve'); }
  };

  const handleRetry = async (taskId) => {
    try {
      await api.put(`/api/linkedin-tasks/${taskId}/retry`);
      toast.success('Task requeued — the extension will retry it');
      loadTasks();
    } catch { toast.error('Failed to retry'); }
  };

  const handleRetryAllFailed = async () => {
    setRetryingAll(true);
    try {
      const res = await api.post('/api/linkedin-tasks/retry-failed');
      const n = res.data?.requeued ?? 0;
      const fixed = res.data?.urls_normalized ?? 0;
      toast.success(`${n} failed task(s) requeued${fixed ? ` · ${fixed} profile URL(s) repaired` : ''}`);
      loadTasks();
    } catch { toast.error('Failed to requeue failed tasks'); }
    finally { setRetryingAll(false); }
  };

  const handleRecheckExpired = async () => {
    setRechecking(true);
    try {
      const res = await api.post('/api/linkedin-tasks/recheck-expired');
      const n = res.data?.rechecked ?? 0;
      const s = res.data?.follow_only_skipped ?? 0;
      toast.success(`${n} expired invite(s) queued for one final accept check${s ? ` · ${s} Follow-only profile(s) re-filed as skipped` : ''}`);
      loadTasks();
    } catch { toast.error('Failed to re-check expired invites'); }
    finally { setRechecking(false); }
  };

  const handleRecheckNow = async () => {
    if (!window.confirm('Run every pending accept-check now (staggered ~5 min apart, read-only profile visits) and add a check for older connects that never had one? No messages are sent.')) return;
    setRecheckingNow(true);
    try {
      const res = await api.post('/api/linkedin-tasks/recheck-accepts-now');
      const p = res.data?.promoted ?? 0;
      const b = res.data?.backfilled ?? 0;
      toast.success(`${p} accept check(s) moved up${b ? ` · ${b} older connect(s) added` : ''} — results land over the next ${Math.max(1, Math.ceil(((p + b) * (res.data?.spacing_min || 5)) / 60))}h while the extension runs`);
      loadTasks();
    } catch { toast.error('Failed to re-check accepts'); }
    finally { setRecheckingNow(false); }
  };

  const handleSendNow = async (taskId) => {
    try {
      await api.put(`/api/linkedin-tasks/${taskId}/send-now`);
      toast.success('Queued to send next — the extension will pick it up on its next check');
      loadTasks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send now');
    }
  };

  const handleRegenerate = async (taskId) => {
    setRegeneratingId(taskId);
    try {
      const res = await api.post(`/api/linkedin-tasks/${taskId}/regenerate`);
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, connection_message: res.data.connection_message } : t));
      toast.success('Message regenerated');
    } catch { toast.error('Regeneration failed'); }
    finally { setRegeneratingId(null); }
  };

  const handleEdit = async (taskId, newMessage) => {
    try {
      await api.put(`/api/linkedin-tasks/${taskId}/message`, { connection_message: newMessage });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, connection_message: newMessage } : t));
      toast.success('Message updated');
    } catch { toast.error('Failed to save edit'); }
  };

  const handleDelete = async (taskId) => {
    try {
      await api.delete(`/api/linkedin-tasks/${taskId}`);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      toast.success('Task deleted');
      loadTasks();
    } catch { toast.error('Failed to delete'); }
  };

  // Filter tasks
  const filtered = tasks.filter(t => {
    if (tab === 'pending') return t.status === 'pending';
    if (tab === 'approval') return t.status === 'needs_approval';
    if (tab === 'scheduled') return t.status === 'scheduled';
    if (tab === 'completed') return t.status === 'completed';
    if (tab === 'failed') return t.status === 'failed';
    if (tab === 'skipped') return t.status === 'skipped';
    if (tab === 'accepted') return t.action === 'check_accept' && t.result === 'accepted';
    return true;
  }).filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (t.lead_name || '').toLowerCase().includes(q) ||
      (t.lead_company || '').toLowerCase().includes(q) ||
      (t.connection_message || '').toLowerCase().includes(q);
  });

  // Accepted connections — newest first, by acceptance time (executed_at).
  const acceptedList = filtered
    .filter(t => t.action === 'check_accept' && t.result === 'accepted')
    .sort((a, b) => new Date(b.executed_at || b.updated_at || 0) - new Date(a.executed_at || a.updated_at || 0));

  // Group by sequence
  const sequences = {};
  const standalone = [];
  filtered.forEach(t => {
    if (t.sequence_id) {
      if (!sequences[t.sequence_id]) sequences[t.sequence_id] = [];
      sequences[t.sequence_id].push(t);
    } else {
      standalone.push(t);
    }
  });
  // Sort sequence steps
  Object.values(sequences).forEach(s => s.sort((a, b) => (a.sequence_step || 0) - (b.sequence_step || 0)));

  return (
    <div className="space-y-6" data-testid="linkedin-outbox-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        {!embedded ? (
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Linkedin className="h-6 w-6 text-sky-500" />
            LinkedIn Outbox
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            AI-generated outreach messages. Copy, edit, and send via LinkedIn.
          </p>
        </div>
        ) : <div />}
        <div className="flex items-center gap-2">
          {tasks.some(t => t.status === 'failed') && (
            <Button variant="outline" onClick={handleRetryAllFailed} disabled={retryingAll}
              className="border-rose-300 text-rose-600 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-400"
              data-testid="retry-all-failed-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${retryingAll ? 'animate-spin' : ''}`} />
              Retry all failed ({tasks.filter(t => t.status === 'failed').length})
            </Button>
          )}
          {(stats?.expired_accepts || 0) > 0 && (
            <Button variant="outline" onClick={handleRecheckExpired} disabled={rechecking}
              className="border-violet-300 text-violet-600 hover:bg-violet-50 dark:border-violet-800 dark:text-violet-400"
              data-testid="recheck-expired-btn">
              <UserPlus className={`h-4 w-4 mr-2 ${rechecking ? 'animate-pulse' : ''}`} />
              Re-check expired invites ({stats.expired_accepts})
            </Button>
          )}
          {(counts?.scheduled || 0) > 0 && (
            <Button variant="outline" onClick={handleRecheckNow} disabled={recheckingNow}
              className="border-emerald-300 text-emerald-600 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400"
              data-testid="recheck-accepts-now-btn">
              <UserCheck className={`h-4 w-4 mr-2 ${recheckingNow ? 'animate-pulse' : ''}`} />
              Re-check accepts now
            </Button>
          )}
          <Button variant="outline" onClick={loadTasks} disabled={loading} data-testid="refresh-outbox-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" onClick={handleExportCsv} data-testid="linkedin-export-csv-btn">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          <Button onClick={() => { setPickerSearch(''); setPickerOpen(true); }} data-testid="outbox-add-task-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Task
          </Button>
        </div>
      </div>

      {/* Extension status banner (Phase 1.1) */}
      <ExtensionStatusBanner />

      {/* Automation controls: restriction pause + send window (Phase 3) */}
      <AutomationBar />

      {/* Stats */}
      <StatsBar stats={stats} />

      {/* Search + Tabs */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, company, or message..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="outbox-search"
          />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-4 sm:grid-cols-8 lg:w-[1000px]">
          <TabsTrigger value="pending" data-testid="tab-pending">
            Pending ({counts ? counts.pending : tasks.filter(t => t.status === 'pending').length})
          </TabsTrigger>
          <TabsTrigger value="approval" data-testid="tab-approval">
            Approval ({counts ? counts.approval : tasks.filter(t => t.status === 'needs_approval').length})
          </TabsTrigger>
          <TabsTrigger value="scheduled" data-testid="tab-scheduled">
            Scheduled ({counts ? counts.scheduled : tasks.filter(t => t.status === 'scheduled').length})
          </TabsTrigger>
          <TabsTrigger value="accepted" data-testid="tab-accepted">
            Accepted ({counts ? counts.accepted : acceptedList.length})
          </TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">
            Completed ({counts ? counts.completed : tasks.filter(t => t.status === 'completed').length})
          </TabsTrigger>
          <TabsTrigger value="failed" data-testid="tab-failed">
            Failed ({counts ? counts.failed : tasks.filter(t => t.status === 'failed').length})
          </TabsTrigger>
          <TabsTrigger value="skipped" data-testid="tab-skipped">
            Skipped ({counts ? counts.skipped : tasks.filter(t => t.status === 'skipped').length})
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">
            All ({counts ? counts.all : tasks.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4 space-y-3">
          {loading ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground mt-2">Loading tasks...</p>
              </CardContent>
            </Card>
          ) : tab === 'accepted' ? (
            acceptedList.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center" data-testid="outbox-accepted-empty">
                  <UserCheck className="h-10 w-10 text-emerald-500/40 mx-auto" />
                  <p className="text-sm font-medium mt-3">No accepted connections yet</p>
                  <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
                    When someone accepts a connection request, they appear here with the date &amp; time — then the sequence unlocks the follow-up message.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2" data-testid="accepted-list">
                {acceptedList.map((t) => (
                  <Card key={t.id} className="border-emerald-500/20 bg-emerald-50/30 dark:bg-emerald-950/10"
                    data-testid={`accepted-row-${t.id}`}>
                    <CardContent className="py-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-9 w-9 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                          <UserCheck className="h-4 w-4 text-emerald-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{t.lead_name || 'Lead'}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {[t.lead_title, t.lead_company].filter(Boolean).join(' · ') || 'Connection accepted'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right">
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Accepted
                          </span>
                          <p className="text-xs text-muted-foreground" data-testid={`accepted-at-${t.id}`}>
                            {sentAtLabel(t.executed_at || t.updated_at) || '—'}
                          </p>
                        </div>
                        {t.linkedin_url && (
                          <a href={t.linkedin_url} target="_blank" rel="noopener noreferrer"
                            className="text-sky-500 hover:text-sky-600" title="Open LinkedIn profile"
                            data-testid={`accepted-profile-${t.id}`}>
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
          ) : filtered.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center" data-testid="outbox-empty">
                <Linkedin className="h-10 w-10 text-muted-foreground/30 mx-auto" />
                <p className="text-sm font-medium mt-3">
                  {search ? 'No tasks match your search' : `No ${tab === 'all' ? '' : tab} LinkedIn tasks yet`}
                </p>
                <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
                  Open a lead with a LinkedIn profile and click <span className="font-medium">Generate LinkedIn Sequence</span>. The AI builds 5-step outreach cadences (connect → engage → message).
                </p>
                <Button size="sm" variant="outline" className="mt-4" onClick={() => window.location.href = '/leads'} data-testid="linkedin-go-pipeline-btn">
                  Go to Pipeline
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Sequence Groups */}
              {Object.entries(sequences).map(([seqId, seqTasks]) => (
                <Card key={seqId} className="border-violet-500/20" data-testid={`sequence-group-${seqId}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-violet-500" />
                      <CardTitle className="text-sm">
                        Sequence: {seqTasks[0]?.lead_name} ({seqTasks.length} steps)
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {seqTasks.map(task => (
                      <TaskCard
                        key={task.id} task={task}
                        onCopy={handleCopy} onToggle={handleToggle} onMarkSent={handleMarkSent}
                        onRegenerate={handleRegenerate} onDelete={handleDelete}
                        onEdit={handleEdit} onApprove={handleApprove} onRetry={handleRetry} onSendNow={handleSendNow} regenerating={regeneratingId === task.id}
                      />
                    ))}
                  </CardContent>
                </Card>
              ))}

              {/* Standalone Tasks */}
              {standalone.map(task => (
                <TaskCard
                  key={task.id} task={task}
                  onCopy={handleCopy} onToggle={handleToggle} onMarkSent={handleMarkSent}
                  onRegenerate={handleRegenerate} onDelete={handleDelete}
                  onEdit={handleEdit} onApprove={handleApprove} onRetry={handleRetry} onSendNow={handleSendNow} regenerating={regeneratingId === task.id}
                />
              ))}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Lead picker for Add Task */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="sm:max-w-md" data-testid="outbox-lead-picker">
          <DialogHeader><DialogTitle>Pick a lead</DialogTitle></DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              autoFocus placeholder="Search leads by name or company..."
              value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)}
              className="pl-9" data-testid="picker-search-input"
            />
          </div>
          <div className="max-h-72 overflow-y-auto -mx-1">
            {pickerLoading ? (
              <div className="text-center py-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-1.5" />Searching…</div>
            ) : pickerResults.length === 0 ? (
              <div className="text-center py-6 text-sm text-muted-foreground">No leads found</div>
            ) : pickerResults.map((l) => (
              <button
                key={l.id} data-testid={`picker-lead-${l.id}`}
                onClick={() => { setManualLead({ id: l.id, name: l.name, linkedin_url: l.linkedin_url }); setPickerOpen(false); }}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-muted text-left transition-colors"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{l.name || 'Unknown'}</p>
                  <p className="text-xs text-muted-foreground truncate">{[l.title, l.company].filter(Boolean).join(' · ')}</p>
                </div>
                {l.linkedin_url ? <Linkedin className="h-3.5 w-3.5 text-sky-500 shrink-0" /> : <UserPlus className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <ManualTaskDialog
        open={!!manualLead}
        onOpenChange={(o) => { if (!o) setManualLead(null); }}
        lead={manualLead}
        onCreated={() => { setManualLead(null); loadTasks(); }}
      />
    </div>
  );
}
